import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Download, WalletCards } from 'lucide-react';
import { useState } from 'react';
import { PayslipBreakdown, type PayrollLine } from '../components/PayslipBreakdown';
import { EmptyState } from '../components/ui/EmptyState';
import { api } from '../lib/api';
import { Loader } from '../components/ui/Spinner';

type MyPayslip = PayrollLine & { periodMonth: string; releasedAt: string };

const money = (v: string) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (period: string) => new Date(`${period}T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

export function MyPayslipsPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const payslips = useQuery({ queryKey: ['my-payslips'], queryFn: async () => (await api.get<MyPayslip[]>('/api/payroll-runs/my-payslips')).data });
  const rows = payslips.data ?? [];

  return (
    <>
      <div className="mb-6"><h1 className="text-3xl font-bold">My Payslips</h1><p className="mt-1 text-muted">Released monthly payslips, most recent first.</p></div>
      {payslips.isLoading && <Loader />}
      {!payslips.isLoading && rows.length === 0 && <EmptyState icon={WalletCards} title="No payslips released yet" hint="Your payslip appears here once HR releases the month's payroll." />}
      <div className="space-y-3">
        {rows.map((line) => {
          const expanded = expandedId === line.id;
          const netPay = line.overrideNetPay ?? line.netPay;
          return (
            <article key={line.id} className="card">
              <div className="flex items-center justify-between gap-3">
                <button type="button" className="flex flex-1 items-center justify-between gap-3 text-left" onClick={() => setExpandedId(expanded ? null : line.id)}>
                  <div>
                    <strong>{monthLabel(line.periodMonth)}</strong>
                    <p className="mt-0.5 text-sm text-muted">Released {new Date(line.releasedAt).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <strong className="text-brand-700">{money(netPay)}</strong>
                    {expanded ? <ChevronUp size={18} className="text-muted" /> : <ChevronDown size={18} className="text-muted" />}
                  </div>
                </button>
                <a
                  className="btn-secondary !px-3 shrink-0"
                  aria-label="Download payslip PDF"
                  href={`${api.defaults.baseURL}/api/payroll-runs/my-payslips/${line.id}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download size={16} />
                </a>
              </div>
              {expanded && <div className="mt-4 border-t border-border pt-4"><PayslipBreakdown line={line} /></div>}
            </article>
          );
        })}
      </div>
    </>
  );
}
